import type { JSX } from 'react';
import { MenuItem } from '@fluentui/react-components';
import { MailRead20Regular, MailUnread20Regular } from '@fluentui/react-icons';
import { useSetMailRead } from '@/hooks/useSetMailRead';

/**
 * Bascule « Marquer comme lu / non lu » du menu contextuel d'un mail rangé.
 *
 * UNE SEULE ENTRÉE QUI CHANGE DE SENS, et non deux entrées dont l'une serait
 * toujours inutile : c'est la convention d'Outlook, et il n'y a de toute façon
 * qu'un état à atteindre depuis celui où l'on est.
 *
 * PARTAGÉE PAR LES TROIS VUES (arbre de l'accueil, grille de la page détail,
 * résultats de recherche) parce que l'état de lecture, lui, est partagé : la
 * pastille s'allume et s'éteint dans les trois d'un seul appel (cf.
 * useSetMailRead, qui écrit dans les deux familles de caches). Trois copies de
 * cette logique auraient dérivé comme les menus contextuels avant
 * ItemActionMenuItems.
 *
 * LE SENS EST DÉDUIT DE CE QUI EST AFFICHÉ, pas de ce que dit le serveur.
 * `isRead === undefined` signifie « on ne sait pas » (back-end antérieur à la
 * migration 0027) et n'affiche AUCUNE pastille : l'entrée propose donc « marquer
 * comme non lu », ce qui est cohérent avec la ligne que l'utilisateur a sous les
 * yeux. Proposer « marquer comme lu » devant un mail d'apparence lue serait
 * incompréhensible.
 */
interface Props {
  mailId: string;
  /** `undefined` = état inconnu ; traité comme « lu », cf. ci-dessus. */
  isRead: boolean | undefined;
  /**
   * Referme le menu appelant, quand la vue le pilote explicitement.
   *
   * OPTIONNEL parce que les trois vues ne s'y prennent pas de la même façon : deux
   * ferment leur menu à la main dans chaque entrée, la troisième s'en remet à
   * l'`onOpenChange` que Fluent déclenche au clic. Imposer le rappel obligerait
   * cette dernière à passer une fonction vide, c'est-à-dire à écrire du code dont
   * le sens est « ne rien faire » — et à le relire un jour comme un oubli.
   */
  onDone?: () => void;
}

export function MailReadMenuItem({ mailId, isRead, onDone }: Props): JSX.Element {
  const setRead = useSetMailRead();
  const unread = isRead === false;
  return (
    <MenuItem
      icon={unread ? <MailRead20Regular /> : <MailUnread20Regular />}
      onClick={() => {
        onDone?.();
        setRead(mailId, unread);
      }}
    >
      {unread ? 'Marquer comme lu' : 'Marquer comme non lu'}
    </MenuItem>
  );
}
